// @flow
import React, {useState} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {Typography, Collapse, IconButton} from '@material-ui/core';
import {ExpandMore, ExpandLess} from '@material-ui/icons';
import {colors} from '../../../../assets/styles/colors';
import GridOptionRenderer from './GridOptionRenderer';
import ListingOptionRenderer from './ListingOptionRenderer';
import TextOptionRenderer from './TextOptionRenderer';
import NumericOptionRenderer from './NumericOptionRenderer';
import type {
  ManualOrderProductOptionGroup,
  SelectedProductOptions,
  ValidationError,
} from '../../types/productOptions';

const useStyles = makeStyles(theme => ({
  optionGroupContainer: {
    marginBottom: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: '.125rem',
    backgroundColor: colors.grey100,
    borderRadius: '.5rem',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  },
  optionGroupHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    padding: '.5rem .5rem 0 .5rem',
    border: 'none',
    width: '100%',
    outline: 'none',
    backgroundColor: 'transparent',

    [theme.breakpoints.up('sm')]: {
      padding: '.75rem .75rem 0 .75rem',
    },

    [theme.breakpoints.up('md')]: {
      padding: '1rem 1rem 0 1rem',
    },
    '&:disabled': {
      cursor: 'default',
    },
  },
  optionGroupTitle: {
    fontSize: '1rem',
    fontWeight: 600,
    color: colors.grey800,
  },
  optionGroupTitleCollapsible: {
    fontSize: '1rem',
    fontWeight: 600,
    color: colors.grey800,
    marginBottom: 0,
  },
  optionGroupDescription: {
    fontSize: '0.875rem',
    color: colors.grey600,
  },
  expandIcon: {
    color: colors.grey600,
    transition: 'transform 0.2s ease',
  },
  optionCount: {
    fontSize: '0.75rem',
    color: colors.grey500,
    marginLeft: theme.spacing(1),
  },
  optionGroupContent: {
    padding: '0 .5rem .5rem .5rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '.5rem',
    [theme.breakpoints.up('sm')]: {
      padding: '0 .75rem .75rem .75rem',
      gap: '.75rem',
    },

    [theme.breakpoints.up('md')]: {
      padding: '0 1rem 1rem 1rem',
    },
  },
  validationErrors: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1),
    backgroundColor: colors.lightRed || '#ffebee',
    borderRadius: '.5rem',
  },
  validationErrorText: {
    fontSize: '0.75rem',
    color: colors.error,
    fontWeight: 500,
    marginBottom: '2px',
    '&:last-child': {
      marginBottom: 0,
    },
  },
}));

export type BaseOptionRendererProps = {
  group: ManualOrderProductOptionGroup,
  selectedProductOptions: SelectedProductOptions,
  currencySymbol: string,
  validationErrors?: ValidationError[],
  onOptionToggle: (groupId: string, optionId: string) => void,
  onTextChange: (groupId: string, optionId: string, value: string) => void,
  onQuantityChange: (
    groupId: string,
    optionId: string,
    quantity: number
  ) => void,
};

// Component for rendering options by type
function OptionsByTypeRenderer({
  group,
  selectedProductOptions,
  currencySymbol,
  validationErrors,
  onOptionToggle,
  onTextChange,
  onQuantityChange,
}: BaseOptionRendererProps) {
  const commonProps = {
    group,
    selectedProductOptions,
    currencySymbol,
    validationErrors,
    onOptionToggle,
    onTextChange,
    onQuantityChange,
  };

  if (group.fieldType === 'TEXT') {
    return <TextOptionRenderer {...commonProps} />;
  }

  if (group.fieldType === 'NUMERIC') {
    return <NumericOptionRenderer {...commonProps} />;
  }

  if (group.config.presentationType === 'GRID') {
    return <GridOptionRenderer {...commonProps} />;
  }

  if (group.config.presentationType === 'LISTING') {
    return <ListingOptionRenderer {...commonProps} />;
  }

  return null;
}

export default function OptionRenderer({
  group,
  selectedProductOptions,
  currencySymbol,
  validationErrors = [],
  onOptionToggle,
  onTextChange,
  onQuantityChange,
}: BaseOptionRendererProps) {
  const classes = useStyles();
  const [isExpanded, setIsExpanded] = useState(false);

  const enabledOptionsCount = group.options.filter(option => option.enabled)
    .length;

  const shouldShowCollapse = enabledOptionsCount >= 2;

  const toggleExpanded = () => {
    if (!shouldShowCollapse) return;
    setIsExpanded(!isExpanded);
  };

  const hasErrors = validationErrors.length > 0;

  return (
    <div className={classes.optionGroupContainer}>
      <button
        className={classes.optionGroupHeader}
        onClick={toggleExpanded}
        disabled={!shouldShowCollapse}
      >
        <div style={{display: 'flex', alignItems: 'center'}}>
          <Typography className={classes.optionGroupTitleCollapsible}>
            {group.storeProductOptionGroup.name}
            {group.required && ' *'}
          </Typography>
          <Typography className={classes.optionCount}>
            ({enabledOptionsCount} opciones)
          </Typography>
        </div>

        <IconButton size="small" className={classes.expandIcon}>
          {isExpanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </button>

      <div
        className={classes.optionGroupContent}
        style={{
          gap: isExpanded ? undefined : '0',
        }}
      >
        {group.storeProductOptionGroup.description && (
          <Typography className={classes.optionGroupDescription}>
            {group.storeProductOptionGroup.description}
          </Typography>
        )}

        {shouldShowCollapse ? (
          <Collapse in={isExpanded}>
            <OptionsByTypeRenderer
              group={group}
              selectedProductOptions={selectedProductOptions}
              currencySymbol={currencySymbol}
              validationErrors={validationErrors}
              onOptionToggle={onOptionToggle}
              onTextChange={onTextChange}
              onQuantityChange={onQuantityChange}
            />
          </Collapse>
        ) : (
          <OptionsByTypeRenderer
            group={group}
            selectedProductOptions={selectedProductOptions}
            currencySymbol={currencySymbol}
            validationErrors={validationErrors}
            onOptionToggle={onOptionToggle}
            onTextChange={onTextChange}
            onQuantityChange={onQuantityChange}
          />
        )}

        {hasErrors && (
          <div className={classes.validationErrors}>
            {validationErrors.map((error, index) => (
              <Typography key={index} className={classes.validationErrorText}>
                {error.message}
              </Typography>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
